import type {
  TeamRepoScore,
  DiscoveryFeatureListView,
  RollupResponse,
  RollupTopUnit,
  RollupLeaderboardRow,
  RollupHighlight,
  RollupHighlights,
  RollupMode,
} from '@sdd-maturity/shared-types';
import { formatFramework } from '@sdd-maturity/shared-types';
import { levelLabel, levelFromScore, normalizeLevel } from './score';
import { isActiveRepo } from './utils';
import { buildFeaturePath, buildRepoPath } from './hierarchy-route';
import type { TeamLeaf, ChildGroup } from './hierarchy';

/*
 * Pure aggregation helpers for the release-train (non-leaf) rollup overview.
 *
 * This module is intentionally free of fixture/network imports so it stays
 * unit-testable and tree-shakeable. Both the mock client (with hand-authored
 * highlight inputs) and the real client (best-effort, no inputs) call these
 * builders with already-fetched per-team data.
 */

export interface PerTeamData {
  team: TeamLeaf;
  repos: TeamRepoScore[];
  features: DiscoveryFeatureListView[];
}

export interface RepoRow {
  teamNumber: string;
  teamName: string;
  repo: TeamRepoScore;
}

export interface FeatureRow {
  teamNumber: string;
  teamName: string;
  feature: DiscoveryFeatureListView;
}

export interface Highlight {
  kind: 'top' | 'improved' | 'adopted';
  label: string;
  itemName: string;
  teamName: string;
  score: number;
  detail: string;
  /** When present, the item name links here (the feature/repo drill-down). */
  href?: string;
}

/**
 * A leaderboard ranks the aggregation unit one tier below the rollup node —
 * Delivery Streams under a Portfolio, Release Trains under a Stream. It is
 * deliberately NEVER an individual scrum team (tier 4): scrum-team-to-scrum-team
 * comparison is suppressed in the UI. `teamCount` is how many leaf teams rolled up.
 */
export interface LeaderboardUnit {
  number: string;
  name: string;
  tier: number;
  teamCount: number;
}

export interface LeaderboardRow {
  unit: LeaderboardUnit;
  avg: number;
  count: number;
  levelCounts: Record<string, number>;
}

export interface ModeRollup<TRow> {
  avg: number;
  count: number;
  top10: TRow[];
  leaderboard: LeaderboardRow[];
  highlights: Highlight[];
  /**
   * Present-levels-only L1–L5 histogram for THIS mode alone — the maturity-
   * distribution bar's ONLY source (AC-1.1/AC-1.3). Sourced from this mode's
   * own already-deduped server `distribution` (or, for the local pure
   * builders below, directly from the flat scored-item list) — NEVER by
   * summing `leaderboard[].levelCounts`, which double-counts a repo/feature
   * whose owning team is mapped under more than one leaderboard row (the
   * 771-vs-897 defect this field replaces).
   */
  levelCounts: Record<string, number>;
}

export interface RollupNode {
  number: string;
  name: string;
  tierLabel?: string;
}

export interface DeliveryHighlightInputs {
  /** key `${org}/${repo}` → points gained since last scan */
  scoreDelta?: Record<string, number>;
  /** key `${org}/${repo}` → framework name just detected */
  newlyAdopted?: Record<string, string>;
}

export interface DiscoveryHighlightInputs {
  /** key `formattedId` → points gained since last scan */
  scoreDelta?: Record<string, number>;
  /** key `formattedId` → true when the feature just reached the SDD bucket */
  newlyAdopted?: Record<string, boolean>;
}

export interface RollupInputs {
  delivery?: DeliveryHighlightInputs;
  discovery?: DiscoveryHighlightInputs;
}

export interface RollupData {
  node: RollupNode;
  teamCount: number;
  repoCount: number;
  featureCount: number;
  deliveryAvg: number;
  discoveryAvg: number;
  delivery: ModeRollup<RepoRow>;
  discovery: ModeRollup<FeatureRow>;
}

/** A feature counts toward Discovery rollups once it has a real (non-stub) score. */
function isScoredFeature(f: DiscoveryFeatureListView): boolean {
  return f.bucket !== 'not-sdd' && (f.overallScore ?? 0) > 0;
}

function repoKey(r: TeamRepoScore): string {
  return `${r.orgName}/${r.repoName}`;
}

function featureName(f: DiscoveryFeatureListView): string {
  return f.name ?? f.formattedId ?? 'Feature';
}

function round(n: number): number {
  return Math.round(n);
}

/**
 * Present-levels-only L1–L5 histogram over a flat, already-deduplicated
 * scored-item list — one entry per repo/feature. Used by the local dev/
 * preview pure builders below, where the flat list (unlike a per-child
 * leaderboard) never double-counts a unit, so a direct count is always safe.
 */
function countLevels<T>(items: T[], levelOf: (item: T) => string): Record<string, number> {
  const levelCounts: Record<string, number> = {};
  for (const item of items) {
    const lvl = levelOf(item);
    levelCounts[lvl] = (levelCounts[lvl] ?? 0) + 1;
  }
  return levelCounts;
}

/** Fallback grouping used when no explicit groups are supplied: each leaf team is
 *  its own (tier-4) unit. Keeps the pure builders usable in isolation/tests. */
function defaultGroups(perTeam: PerTeamData[]): ChildGroup[] {
  return perTeam.map((pt) => ({
    unit: { number: pt.team.number, name: pt.team.name, tier: 4 },
    leaves: [pt.team],
  }));
}

/** Aggregate per-leaf scored items into one ranked leaderboard row per group. */
function buildLeaderboard(
  groups: ChildGroup[],
  scoredByLeaf: Map<string, Array<{ score: number; level: string }>>,
): LeaderboardRow[] {
  const out: LeaderboardRow[] = [];
  for (const g of groups) {
    const levelCounts: Record<string, number> = {};
    let sum = 0;
    let count = 0;
    for (const leaf of g.leaves) {
      for (const item of scoredByLeaf.get(leaf.number) ?? []) {
        levelCounts[item.level] = (levelCounts[item.level] ?? 0) + 1;
        sum += item.score;
        count += 1;
      }
    }
    if (count === 0) continue;
    out.push({
      unit: { ...g.unit, teamCount: g.leaves.length },
      avg: round(sum / count),
      count,
      levelCounts,
    });
  }
  out.sort((a, b) => b.avg - a.avg);
  return out;
}

function buildDeliveryHighlights(rows: RepoRow[], inputs: DeliveryHighlightInputs): Highlight[] {
  if (rows.length === 0) return [];
  const highlights: Highlight[] = [];
  // Canonical leaf URL only; without an owning team the highlight has no link.
  const repoHref = (r: RepoRow): string | undefined =>
    r.teamNumber ? buildRepoPath(r.teamNumber, r.repo.orgName, r.repo.repoName) : undefined;

  const top = [...rows].sort((a, b) => b.repo.overallScore - a.repo.overallScore)[0]!;
  highlights.push({
    kind: 'top',
    label: 'Top performer',
    itemName: top.repo.repoName,
    teamName: top.teamName,
    score: top.repo.overallScore,
    detail: `${levelLabel(top.repo.overallLevel)} · ${formatFramework(top.repo.framework)}`,
    href: repoHref(top),
  });

  const delta = inputs.scoreDelta ?? {};
  const withDelta = rows.filter((r) => (delta[repoKey(r.repo)] ?? 0) > 0);
  const improved =
    withDelta.sort((a, b) => (delta[repoKey(b.repo)] ?? 0) - (delta[repoKey(a.repo)] ?? 0))[0] ??
    [...rows].sort((a, b) => b.repo.findingsCount.resolved - a.repo.findingsCount.resolved)[0];
  if (improved) {
    const d = delta[repoKey(improved.repo)];
    highlights.push({
      kind: 'improved',
      label: 'Most improved',
      itemName: improved.repo.repoName,
      teamName: improved.teamName,
      score: improved.repo.overallScore,
      detail: d != null ? `▲ ${d} pts since last scan` : `${improved.repo.findingsCount.resolved} findings resolved`,
      href: repoHref(improved),
    });
  }

  const adopt = inputs.newlyAdopted ?? {};
  const adopted = rows.find((r) => adopt[repoKey(r.repo)]);
  if (adopted) {
    highlights.push({
      kind: 'adopted',
      label: 'Newly adopted',
      itemName: adopted.repo.repoName,
      teamName: adopted.teamName,
      score: adopted.repo.overallScore,
      detail: `Adopted ${formatFramework(adopt[repoKey(adopted.repo)]!)}`,
      href: repoHref(adopted),
    });
  }

  return highlights;
}

function buildDiscoveryHighlights(rows: FeatureRow[], inputs: DiscoveryHighlightInputs): Highlight[] {
  if (rows.length === 0) return [];
  const highlights: Highlight[] = [];
  const score = (r: FeatureRow): number => r.feature.overallScore ?? 0;
  const fid = (r: FeatureRow): string => r.feature.formattedId ?? '';
  // Canonical leaf URL only; needs both a formattedId and an owning team.
  const featureHref = (r: FeatureRow): string | undefined =>
    fid(r) && r.teamNumber ? buildFeaturePath(r.teamNumber, fid(r)) : undefined;

  const top = [...rows].sort((a, b) => score(b) - score(a))[0]!;
  highlights.push({
    kind: 'top',
    label: 'Top performer',
    itemName: featureName(top.feature),
    teamName: top.teamName,
    score: score(top),
    detail: levelLabel(normalizeLevel(top.feature.level) || 'L1'),
    href: featureHref(top),
  });

  const delta = inputs.scoreDelta ?? {};
  const withDelta = rows.filter((r) => (delta[fid(r)] ?? 0) > 0);
  const improved =
    withDelta.sort((a, b) => (delta[fid(b)] ?? 0) - (delta[fid(a)] ?? 0))[0] ??
    [...rows].sort((a, b) => new Date(b.feature.timestamp ?? 0).getTime() - new Date(a.feature.timestamp ?? 0).getTime())[0];
  if (improved) {
    const d = delta[fid(improved)];
    highlights.push({
      kind: 'improved',
      label: 'Most improved',
      itemName: featureName(improved.feature),
      teamName: improved.teamName,
      score: score(improved),
      detail: d != null ? `▲ ${d} pts since last scan` : 'Recently re-scored',
      href: featureHref(improved),
    });
  }

  const adopt = inputs.newlyAdopted ?? {};
  const adopted =
    rows.find((r) => adopt[fid(r)]) ??
    rows.find((r) => r.feature.bucket === 'sdd-discovery' && r !== top);
  if (adopted) {
    highlights.push({
      kind: 'adopted',
      label: 'Newly adopted',
      itemName: featureName(adopted.feature),
      teamName: adopted.teamName,
      score: score(adopted),
      detail: 'Reached SDD bucket',
      href: featureHref(adopted),
    });
  }

  return highlights;
}

export function buildDeliveryRollup(
  perTeam: PerTeamData[],
  groups?: ChildGroup[],
  inputs: DeliveryHighlightInputs = {},
): ModeRollup<RepoRow> {
  const rows: RepoRow[] = [];
  const scoredByLeaf = new Map<string, Array<{ score: number; level: string }>>();

  for (const pt of perTeam) {
    const scored = pt.repos.filter(isActiveRepo);
    if (scored.length === 0) continue;
    scoredByLeaf.set(
      pt.team.number,
      scored.map((r) => ({ score: r.overallScore, level: r.overallLevel })),
    );
    for (const r of scored) {
      rows.push({ teamNumber: pt.team.number, teamName: pt.team.name, repo: r });
    }
  }

  const leaderboard = buildLeaderboard(groups ?? defaultGroups(perTeam), scoredByLeaf);
  const avg = rows.length ? round(rows.reduce((s, r) => s + r.repo.overallScore, 0) / rows.length) : 0;
  const top10 = [...rows].sort((a, b) => b.repo.overallScore - a.repo.overallScore).slice(0, 10);
  const levelCounts = countLevels(rows, (r) => r.repo.overallLevel);

  return { avg, count: rows.length, top10, leaderboard, highlights: buildDeliveryHighlights(rows, inputs), levelCounts };
}

export function buildDiscoveryRollup(
  perTeam: PerTeamData[],
  groups?: ChildGroup[],
  inputs: DiscoveryHighlightInputs = {},
): ModeRollup<FeatureRow> {
  const rows: FeatureRow[] = [];
  const scoredByLeaf = new Map<string, Array<{ score: number; level: string }>>();

  for (const pt of perTeam) {
    const scored = pt.features.filter(isScoredFeature);
    if (scored.length === 0) continue;
    scoredByLeaf.set(
      pt.team.number,
      scored.map((f) => ({ score: f.overallScore ?? 0, level: normalizeLevel(f.level) || 'L1' })),
    );
    for (const f of scored) {
      rows.push({ teamNumber: pt.team.number, teamName: pt.team.name, feature: f });
    }
  }

  const leaderboard = buildLeaderboard(groups ?? defaultGroups(perTeam), scoredByLeaf);
  const avg = rows.length ? round(rows.reduce((s, r) => s + (r.feature.overallScore ?? 0), 0) / rows.length) : 0;
  const top10 = [...rows]
    .sort((a, b) => (b.feature.overallScore ?? 0) - (a.feature.overallScore ?? 0))
    .slice(0, 10);
  const levelCounts = countLevels(rows, (r) => normalizeLevel(r.feature.level) || 'L1');

  return { avg, count: rows.length, top10, leaderboard, highlights: buildDiscoveryHighlights(rows, inputs), levelCounts };
}

export function buildRollup(
  node: RollupNode,
  perTeam: PerTeamData[],
  groups?: ChildGroup[],
  inputs: RollupInputs = {},
): RollupData {
  const delivery = buildDeliveryRollup(perTeam, groups, inputs.delivery);
  const discovery = buildDiscoveryRollup(perTeam, groups, inputs.discovery);

  return {
    node,
    teamCount: perTeam.length,
    repoCount: delivery.count,
    featureCount: discovery.count,
    deliveryAvg: delivery.avg,
    discoveryAvg: discovery.avg,
    delivery,
    discovery,
  };
}

/*
 * Server-rollup → dashboard-RollupData adapter (TASK-6 — FR-1 → AC-1.2).
 *
 * The rich rollup is now precomputed server-side and served as two `RollupResponse`s
 * (one per `?mode=`), replacing the old client-side team-endpoint fan-out. This pure
 * adapter reshapes those two responses into the exact `RollupData` the #629 components
 * (Overview, PhaseView, Top10List, TeamLeaderboard, RepoMaturityDistribution) already
 * consume — so no component prop changes. It never fetches or mutates.
 */

/** Canonical maturity level for a unit — normalize the server label, else derive
 *  from the score so a top-ranked unit always renders a level badge. */
function unitLevel(u: { level?: string; score: number }): string {
  return normalizeLevel(u.level) || levelFromScore(u.score);
}

/** A Delivery `topUnit` → the `RepoRow` PhaseView reads (repoName/orgName/overallScore/
 *  overallLevel + owning team for the canonical drill-down link). */
function topUnitToRepoRow(u: RollupTopUnit): RepoRow {
  return {
    teamNumber: u.teamNumber ?? '',
    teamName: u.team,
    repo: {
      orgName: u.orgName ?? '',
      repoName: u.repoName ?? u.unit,
      overallScore: u.score,
      overallLevel: unitLevel(u) as TeamRepoScore['overallLevel'],
      framework: u.framework ?? '',
      dimensions: [],
      lastScanTimestamp: '',
      findingsCount: { open: 0, resolved: 0 },
      findingsDigest: [],
    },
  };
}

/** A Discovery `topUnit` → the `FeatureRow` PhaseView reads (formattedId/name/
 *  overallScore/level + owning team for the canonical drill-down link). */
function topUnitToFeatureRow(u: RollupTopUnit): FeatureRow {
  const level = unitLevel(u);
  return {
    teamNumber: u.teamNumber ?? '',
    teamName: u.team,
    feature: {
      teamNumber: u.teamNumber ?? '',
      formattedId: u.formattedId ?? '',
      featureFormattedId: u.formattedId ?? '',
      featureName: u.unit,
      name: u.unit,
      classificationBucket: u.bucket,
      bucket: u.bucket,
      overallScore: u.score,
      overallLevel: level,
      level,
      scanTimestamp: '',
      timestamp: '',
      dimensions: [],
      lowConfidence: false,
    },
  };
}

/** Server leaderboard row → dashboard `LeaderboardRow` (null avg → 0 for scoreColor;
 *  the fixed-key L1–L5 counts widen to the `Record<string, number>` the bar reads). */
function toLeaderboardRow(row: RollupLeaderboardRow): LeaderboardRow {
  return {
    unit: {
      number: row.unit.number,
      name: row.unit.name,
      tier: row.unit.tier,
      teamCount: row.unit.teamCount,
    },
    avg: round(row.avg ?? 0),
    count: row.count,
    levelCounts: { ...row.levelCounts },
  };
}

/** Canonical drill-down href for a highlight from its id components: a Delivery
 *  repo needs team+org+repo, a Discovery feature needs team+formattedId. */
function highlightHref(h: RollupHighlight, mode: RollupMode): string | undefined {
  if (mode === 'delivery') {
    return h.teamNumber && h.orgName && h.repoName
      ? buildRepoPath(h.teamNumber, h.orgName, h.repoName)
      : undefined;
  }
  return h.teamNumber && h.formattedId ? buildFeaturePath(h.teamNumber, h.formattedId) : undefined;
}

/** The three server momentum highlights → the positive-only `Highlight[]` the
 *  HighlightCard band renders. Null slots (single-scan fallback, AC-10.4) are
 *  dropped. Detail mirrors the legacy builders: level (+framework for Delivery top),
 *  a `▲ N pts` delta for Most Improved, and the adopted-framework/SDD-bucket note. */
function mapHighlights(h: RollupHighlights, mode: RollupMode): Highlight[] {
  const out: Highlight[] = [];
  const push = (
    src: RollupHighlight | null,
    kind: Highlight['kind'],
    label: string,
    detail: (x: RollupHighlight) => string,
  ): void => {
    if (!src) return;
    out.push({
      kind,
      label,
      itemName: src.unit,
      teamName: src.team,
      score: src.score,
      detail: detail(src),
      href: highlightHref(src, mode),
    });
  };
  const lvl = (x: RollupHighlight): string => normalizeLevel(x.level) || levelFromScore(x.score);

  push(h.topPerformer, 'top', 'Top performer', (x) =>
    mode === 'delivery' && x.framework ? `${levelLabel(lvl(x))} · ${formatFramework(x.framework)}` : levelLabel(lvl(x)),
  );
  push(h.mostImproved, 'improved', 'Most improved', (x) =>
    x.delta != null ? `▲ ${x.delta} pts since last scan` : levelLabel(lvl(x)),
  );
  push(h.newlyAdopted, 'adopted', 'Newly adopted', (x) =>
    mode === 'delivery'
      ? x.framework
        ? `Adopted ${formatFramework(x.framework)}`
        : 'Newly adopted'
      : 'Reached SDD bucket',
  );

  return out;
}

/**
 * Present-levels-only L1–L5 histogram from a mode's own server `distribution`
 * (AC-1.1, AC-1.3) — the maturity-distribution bar's ONLY source. Deliberately
 * NOT derived by summing `leaderboard[].levelCounts`: the leaderboard has one
 * row per CHILD SCOPE, and a repo/feature whose owning team is mapped under
 * more than one child scope is counted once per row there — re-summing it
 * reintroduces exactly the 771-vs-897 phantom-population defect this adapter
 * exists to remove. `distribution` is already deduped at the leaf-team level
 * server-side, so a direct present-levels-only read is the whole fix.
 */
function distributionLevelCounts(distribution: RollupResponse['distribution']): Record<string, number> {
  const levelCounts: Record<string, number> = {};
  for (const [level, seg] of Object.entries(distribution)) {
    if (seg.count > 0) levelCounts[level] = seg.count;
  }
  return levelCounts;
}

/**
 * Map the two per-mode `RollupResponse`s (delivery + discovery) onto the single
 * `RollupData` the dashboard components consume. Pure — no fetch, no mutation.
 *
 * `counts.teams` is mode-independent (both responses agree), so it is read from the
 * Delivery response. `counts.repos`/`counts.features` are NOT mode-independent —
 * each mode's materialized rollup only populates its own unit type and zeroes the
 * other (see `loadLeafScoreRows`/`buildMaterializedRollup` in rollup-sweep/persist.ts),
 * so `repoCount` must come from `delivery.counts.repos` and `featureCount` from
 * `discovery.counts.features`. Nullable `averages` (AC-2.4 "—" semantics) are coerced
 * to `0` here because `RollupData`'s avg fields are typed `number` and the components
 * already treat `0` as the empty case. Rounded via the same `round()` `buildLeaderboard`
 * already uses — the server's raw division mean (e.g. `35.169642857...`) must not
 * reach display components unrounded.
 */
export function rollupResponseToRollupData(
  delivery: RollupResponse,
  discovery: RollupResponse,
): RollupData {
  const { counts } = delivery;

  const deliveryAvg = round(delivery.averages.delivery ?? 0);
  const discoveryAvg = round(discovery.averages.discovery ?? 0);

  const deliveryRollup: ModeRollup<RepoRow> = {
    avg: deliveryAvg,
    count: delivery.topUnitsTotal,
    top10: delivery.topUnits.map(topUnitToRepoRow),
    leaderboard: delivery.leaderboard.map(toLeaderboardRow),
    highlights: mapHighlights(delivery.highlights, 'delivery'),
    levelCounts: distributionLevelCounts(delivery.distribution),
  };

  const discoveryRollup: ModeRollup<FeatureRow> = {
    avg: discoveryAvg,
    count: discovery.topUnitsTotal,
    top10: discovery.topUnits.map(topUnitToFeatureRow),
    leaderboard: discovery.leaderboard.map(toLeaderboardRow),
    highlights: mapHighlights(discovery.highlights, 'discovery'),
    levelCounts: distributionLevelCounts(discovery.distribution),
  };

  return {
    node: { number: delivery.scope.teamNumber, name: delivery.scope.name },
    teamCount: counts.teams,
    repoCount: counts.repos,
    featureCount: discovery.counts.features,
    deliveryAvg,
    discoveryAvg,
    delivery: deliveryRollup,
    discovery: discoveryRollup,
  };
}
